import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { apiGet, type AuthUser } from "../api.js";

interface AuditEvent {
  id: string;
  klien_id: string | null;
  actor_id: string;
  actor_role: string;
  actor_scope_type: string;
  event_type: string;
  finger: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  sequence: string;
  this_hash: string;
  previous_hash: string | null;
  signed_at: string;
}

interface AuditListResponse {
  items: AuditEvent[];
  total: number;
  page: number;
  per_page: number;
}

interface VerifyResponse {
  ok: boolean;
  broken_at_sequence: string | null;
}

interface AuditPageProps {
  user: AuthUser;
}

const EVENT_LABEL: Record<string, string> = {
  klien_created: "Klien dibuat",
  klien_approved: "Klien disetujui",
  klien_flagged_back: "Klien ditarik kembali",
  approved_finger: "Approve jari",
  edited_pattern: "Edit pola",
  edited_ridge: "Edit sulur",
  edited_core: "Edit core",
  edited_delta: "Edit delta",
  share_issued: "Tautan dibuat",
  share_revoked: "Tautan dicabut",
  otp_sent: "OTP dikirim",
  otp_verified: "OTP diverifikasi",
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("id-ID", { hour12: false });
  } catch {
    return iso;
  }
}

function fmtBeforeAfter(before: unknown, after: unknown): string {
  if (!before && !after) return "—";
  const fmt = (v: unknown) => {
    if (v == null) return "∅";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return `${fmt(before)} → ${fmt(after)}`;
}

export function AuditPage({ user }: AuditPageProps) {
  const [klienFilter, setKlienFilter] = useState("");
  const { data, isLoading, error, refetch } = useQuery<AuditListResponse>({
    queryKey: ["audit-events", klienFilter],
    queryFn: () => {
      const q = klienFilter ? `?klien_id=${encodeURIComponent(klienFilter)}&per_page=100` : "?per_page=100";
      return apiGet<AuditListResponse>(`api/audit/events${q}`);
    },
    refetchInterval: 15_000,
  });

  const canVerify = user.role === "owner" || user.role === "super_admin";
  const [verifyResult, setVerifyResult] = useState<VerifyResponse | null>(null);
  const verifyMutation = useMutation({
    mutationFn: () => apiGet<VerifyResponse>("api/audit/verify-chain"),
    onSuccess: (r) => setVerifyResult(r),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{data?.total ?? 0} kejadian</span>
          <input
            type="text"
            placeholder="Filter klien_id…"
            value={klienFilter}
            onChange={(e) => setKlienFilter(e.target.value)}
            className="input text-xs w-72"
          />
          <button type="button" onClick={() => refetch()} className="btn-ghost text-xs">Refresh</button>
          {canVerify && (
            <button
              type="button"
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending}
              className="btn-primary text-xs"
            >
              {verifyMutation.isPending ? "Verifying…" : "Verify chain"}
            </button>
          )}
        </div>
      </div>

      {verifyResult && (
        <div
          className={`card text-sm ${
            verifyResult.ok ? "bg-green-50 border-green-200 text-green-900" : "bg-red-50 border-red-200 text-tabrakan"
          }`}
        >
          {verifyResult.ok
            ? "✅ Audit chain utuh — hash signature semua kejadian valid."
            : `❌ Chain rusak di sequence #${verifyResult.broken_at_sequence}`}
        </div>
      )}

      {isLoading && <div className="card text-center text-slate-500">Loading…</div>}
      {error && <div className="card text-tabrakan text-sm">Error: {error instanceof Error ? error.message : String(error)}</div>}

      {data && data.items.length === 0 && (
        <div className="card text-center text-slate-500">Belum ada kejadian audit.</div>
      )}

      {data && data.items.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Waktu</th>
                <th className="px-3 py-2 text-left font-medium">Kejadian</th>
                <th className="px-3 py-2 text-left font-medium">Jari</th>
                <th className="px-3 py-2 text-left font-medium">Perubahan</th>
                <th className="px-3 py-2 text-left font-medium">Aktor</th>
                <th className="px-3 py-2 text-left font-medium">Klien</th>
                <th className="px-3 py-2 text-left font-medium">Seq · Hash</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtDate(e.signed_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-900">
                    {EVENT_LABEL[e.event_type] ?? e.event_type}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{e.finger ?? "—"}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-md truncate" title={fmtBeforeAfter(e.before, e.after)}>
                    {fmtBeforeAfter(e.before, e.after)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                    <span className="px-2 py-0.5 rounded text-[10px] bg-slate-100">{e.actor_role}</span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500 font-mono text-[10px]">
                    {e.klien_id ? e.klien_id.slice(0, 8) : "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500 font-mono text-[10px]">
                    #{Number(e.sequence)} · {e.this_hash.slice(0, 12)}…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Audit log immutable dengan signature SHA-256 berantai. Setiap kejadian tercatat dengan link ke kejadian
        sebelumnya — modifikasi otomatis terdeteksi via "Verify chain".
      </p>
    </div>
  );
}
